import { useSettings } from "../context/SettingsContext";

export default function RateSettings() {
  const { settings, setSettings } = useSettings();

  if (!settings) return <p>Loading settings...</p>;

  const baseRates = settings.baseRates || {
    fuelRatePerKm: 0,
    baseTransportRatePerKm: 0,
    tollPerGate: 0,
    escortRatePerKm: 0
  };

  const rateRules = settings.rateRules || [];
  const classificationRules = settings.classificationRules || [];
  const escortRules = settings.escortRules || [];
  const pricingOptions = settings.pricingOptions || {
    minQuoteAmount: 12000,
    loadingOffloadFee: 3500,
    escortMinimumPerVehicle: 2800,
    policeEscortFee: 6500,
    tollKmPerGate: 65
  };
  const distanceRules = settings.distanceRules || {
    longDistanceThreshold: 500,
    longDistanceDiscount: 0.9
  };

  const updatePricingOption = (key, value) => {
    setSettings({
      ...settings,
      pricingOptions: {
        ...pricingOptions,
        [key]: Number(value)
      }
    });
  };

  const updateDistanceRule = (key, value) => {
    setSettings({
      ...settings,
      distanceRules: {
        ...distanceRules,
        [key]: Number(value)
      }
    });
  };

  /* ================= UPDATE FUNCTIONS ================= */

  const updateBase = (key, value) => {
    setSettings({
      ...settings,
      baseRates: {
        ...baseRates,
        [key]: Number(value)
      }
    });
  };

  const updateRateRule = (i, field, value) => {
    const updated = rateRules.map((rule, idx) =>
      idx === i ? { ...rule, [field]: value } : rule
    );
    setSettings({ ...settings, rateRules: updated });
  };

  const updateRateCondition = (i, key, value) => {
    const updated = rateRules.map((rule, idx) =>
      idx === i
        ? {
            ...rule,
            conditions: {
              ...(rule.conditions || {}),
              [key]: Number(value)
            }
          }
        : rule
    );
    setSettings({ ...settings, rateRules: updated });
  };

  const addRateRule = () => {
    setSettings({
      ...settings,
      rateRules: [...rateRules, { name: "", ratePerKm: 0, conditions: {} }]
    });
  };

  const removeRateRule = (i) => {
    setSettings({
      ...settings,
      rateRules: rateRules.filter((_, idx) => idx !== i)
    });
  };

  const updateClass = (i, field, value) => {
    const updated = classificationRules.map((rule, idx) =>
      idx === i ? { ...rule, [field]: value } : rule
    );
    setSettings({ ...settings, classificationRules: updated });
  };

  const addClass = () => {
    setSettings({
      ...settings,
      classificationRules: [
        ...classificationRules,
        { name: "", factor: 1, risk: "low", conditions: {} }
      ]
    });
  };

  const removeClass = (i) => {
    setSettings({
      ...settings,
      classificationRules: classificationRules.filter((_, idx) => idx !== i)
    });
  };

  const updateEscort = (i, key, value) => {
    const updated = escortRules.map((rule, idx) =>
      idx === i ? { ...rule, [key]: Number(value) } : rule
    );
    setSettings({ ...settings, escortRules: updated });
  };

  const addEscort = () => {
    setSettings({
      ...settings,
      escortRules: [...escortRules, { minWidth: 0, vehicles: 0 }]
    });
  };

  const removeEscort = (i) => {
    setSettings({
      ...settings,
      escortRules: escortRules.filter((_, idx) => idx !== i)
    });
  };

  return (
    <div className="settings-wrapper">

      {/* ================= ACTIVE SETTINGS ================= */}
      <div className="settings-section preview">
        <h3>📊 Active System Settings</h3>

        <div className="preview-grid">
          <div>
            <strong>Fuel Rate</strong>
            <p>{baseRates.fuelRatePerKm} / km</p>
          </div>
          <div>
            <strong>Transport Rate</strong>
            <p>{baseRates.baseTransportRatePerKm} / km</p>
          </div>
          <div>
            <strong>Toll / Gate</strong>
            <p>{baseRates.tollPerGate}</p>
          </div>
          <div>
            <strong>Escort Rate</strong>
            <p>{baseRates.escortRatePerKm} / km</p>
          </div>
        </div>

        <div className="preview-sub">
          <h4>Classification</h4>
          {classificationRules.map((c, i) => (
            <div key={i} className="preview-row">
              <span>{c.name}</span>
              <span>{c.factor}x</span>
              <span className={`risk ${c.risk}`}>{c.risk}</span>
            </div>
          ))}
        </div>

        <div className="preview-sub">
          <h4>Escort Rules</h4>
          {escortRules.map((e, i) => (
            <div key={i} className="preview-row">
              <span>Width ≥ {e.minWidth}m</span>
              <span>{e.vehicles} Vehicles</span>
            </div>
          ))}
        </div>
      </div>

      {/* ================= BASE RATES ================= */}
      <div className="settings-section">
        <h3>🚛 Transport Pricing</h3>

        <div className="grid">
          <div className="field">
            <label>Base Transport Rate (per KM)</label>
            <input
              type="number"
              step="any"
              value={baseRates.baseTransportRatePerKm}
              onChange={(e) => updateBase("baseTransportRatePerKm", e.target.value)}
            />
          </div>

          <div className="field">
            <label>Fuel Cost per KM</label>
            <input
              type="number"
              step="any"
              value={baseRates.fuelRatePerKm}
              onChange={(e) => updateBase("fuelRatePerKm", e.target.value)}
            />
          </div>

          <div className="field">
            <label>Toll Cost per Gate</label>
            <input
              type="number"
              step="any"
              value={baseRates.tollPerGate}
              onChange={(e) => updateBase("tollPerGate", e.target.value)}
            />
          </div>

          <div className="field">
            <label>Escort Cost per KM</label>
            <input
              type="number"
              step="any"
              value={baseRates.escortRatePerKm}
              onChange={(e) => updateBase("escortRatePerKm", e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ================= ESCORT RULES ================= */}
      <div className="settings-section">
        <h3>🚓 Escort Rules</h3>

        {escortRules.map((e, i) => (
          <div key={i} className="rule-box">
            <div className="grid">
              <input
                type="number"
                step="any"
                value={e.minWidth}
                onChange={(ev) => updateEscort(i, "minWidth", ev.target.value)}
                placeholder="Min Width"
                aria-label={`Escort rule ${i + 1} minimum width`}
              />
              <input
                type="number"
                step="1"
                value={e.vehicles}
                onChange={(ev) => updateEscort(i, "vehicles", ev.target.value)}
                placeholder="Vehicles"
                aria-label={`Escort rule ${i + 1} vehicles`}
              />
            </div>

            <button onClick={() => removeEscort(i)}>Remove</button>
          </div>
        ))}

        <button onClick={addEscort}>+ Add Escort Rule</button>
      </div>

      {/* ================= CLASSIFICATION ================= */}
      <div className="settings-section">
        <h3>📦 Classification</h3>

        {classificationRules.map((c, i) => (
          <div key={i} className="rule-box">
            <div className="grid">
              <input
                value={c.name}
                onChange={(e) => updateClass(i, "name", e.target.value)}
                aria-label={`Classification rule ${i + 1} name`}
                placeholder="Classification name"
              />
              <input
                type="number"
                step="any"
                value={c.factor}
                onChange={(e) => updateClass(i, "factor", e.target.value)}
                aria-label={`Classification rule ${i + 1} factor`}
                placeholder="Factor"
              />
              <input
                value={c.risk}
                onChange={(e) => updateClass(i, "risk", e.target.value)}
                aria-label={`Classification rule ${i + 1} risk level`}
                placeholder="Risk level"
              />
            </div>

            <button onClick={() => removeClass(i)}>Remove</button>
          </div>
        ))}

        <button onClick={addClass}>+ Add Classification</button>
      </div>

      {/* ================= QUOTE OPTIONS ================= */}
      <div className="settings-section">
        <h3>Quote options</h3>
        <div className="grid">
          <div className="field">
            <label>Minimum quote (ZAR)</label>
            <input
              type="number"
              step="any"
              value={pricingOptions.minQuoteAmount ?? ""}
              onChange={(e) => updatePricingOption("minQuoteAmount", e.target.value)}
            />
          </div>
          <div className="field">
            <label>Loading / offloading fee</label>
            <input
              type="number"
              step="any"
              value={pricingOptions.loadingOffloadFee ?? ""}
              onChange={(e) => updatePricingOption("loadingOffloadFee", e.target.value)}
            />
          </div>
          <div className="field">
            <label>Long haul threshold (km)</label>
            <input
              type="number"
              step="any"
              value={distanceRules.longDistanceThreshold ?? ""}
              onChange={(e) => updateDistanceRule("longDistanceThreshold", e.target.value)}
            />
          </div>
          <div className="field">
            <label>Long haul discount (0.9 = 10% off)</label>
            <input
              type="number"
              step="0.01"
              value={distanceRules.longDistanceDiscount ?? ""}
              onChange={(e) => updateDistanceRule("longDistanceDiscount", e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ================= RATE RULES ================= */}
      <div className="settings-section">
        <h3>💰 Dynamic Rate Rules</h3>

        {rateRules.map((r, i) => (
          <div key={i} className="rule-box">
            <div className="grid">
              <input
                value={r.name}
                onChange={(e) => updateRateRule(i, "name", e.target.value)}
                aria-label={`Rate rule ${i + 1} name`}
                placeholder="Rule name"
              />
              <input
                type="number"
                step="any"
                value={r.ratePerKm}
                onChange={(e) => updateRateRule(i, "ratePerKm", e.target.value)}
                aria-label={`Rate rule ${i + 1} rate per km`}
                placeholder="Rate per km"
              />
              <input
                type="number"
                step="any"
                value={r.conditions?.maxWeight || ""}
                onChange={(e) => updateRateCondition(i, "maxWeight", e.target.value)}
                placeholder="Max Weight"
                aria-label={`Rate rule ${i + 1} max weight`}
              />
            </div>

            <button onClick={() => removeRateRule(i)}>Remove</button>
          </div>
        ))}

        <button onClick={addRateRule}>+ Add Rate Rule</button>
      </div>

    </div>
  );
}